import { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  LayoutDashboard,
  Megaphone,
  Users,
  FileText,
  MessageCircleQuestion,
  Rocket,
  BookOpen,
  KeyRound,
  Menu,
  X,
} from 'lucide-react';

const ICON = 'h-[18px] w-[18px]';
const LINK_BASE =
  'flex items-center gap-3 rounded-btn px-3 py-2.5 text-sm font-medium transition-colors duration-150';
const LINK_ACTIVE = 'bg-primary/10 text-primary';
const LINK_IDLE =
  'text-base-content/70 hover:bg-base-300 hover:text-base-content';

export const AdminLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    {
      to: '/admin',
      icon: LayoutDashboard,
      label: t('admin.dashboard'),
      exact: true,
    },
    {
      to: '/admin/merchants',
      icon: Users,
      label: t('admin.merchants'),
      exact: false,
    },
    { to: '/admin/wiki', icon: BookOpen, label: 'Wiki', exact: false },
    {
      to: '/admin/changelog',
      icon: Megaphone,
      label: t('admin.changelog.title'),
      exact: true,
    },
    {
      to: '/admin/exs-lizenzen',
      icon: KeyRound,
      label: t('admin.exsLicenses.title'),
      exact: false,
    },
    {
      to: '/admin/kb',
      icon: MessageCircleQuestion,
      label: t('admin.kb.title'),
      exact: true,
    },
    {
      to: '/admin/setup-wizard',
      icon: Rocket,
      label: t('admin.setupWizard.title'),
      exact: true,
    },
    {
      to: '/admin/audit',
      icon: FileText,
      label: t('admin.auditLogs'),
      exact: true,
    },
  ];

  const isActive = (path: string, exact: boolean) =>
    exact ? location.pathname === path : location.pathname.startsWith(path);

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-base-300 px-4 py-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-btn bg-primary/10">
          <span className="text-sm font-bold text-primary">GO</span>
        </div>
        <span className="text-lg font-semibold">{t('admin.adminPanel')}</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3 py-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.to, item.exact);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`${LINK_BASE} ${active ? LINK_ACTIVE : LINK_IDLE}`}
              onClick={() => setSidebarOpen(false)}
            >
              <Icon className={ICON} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-base-300 px-3 py-4">
        <Link
          to="/dashboard"
          className={`${LINK_BASE} ${LINK_IDLE}`}
          onClick={() => setSidebarOpen(false)}
        >
          <ArrowLeft className={ICON} />
          {t('admin.backToDashboard')}
        </Link>
      </div>
    </div>
  );

  return (
    <div className="admin-layout flex h-screen bg-base-100">
      {/* Desktop Sidebar */}
      <aside className="hidden w-64 flex-col border-r border-base-300 bg-base-200 lg:flex">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <aside
            className="absolute left-0 top-0 h-full w-64 animate-slide-in bg-base-200 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="flex h-14 items-center gap-3 border-b border-base-300 bg-base-200 px-4 lg:hidden">
          <button
            className="flex h-10 w-10 items-center justify-center rounded-btn text-base-content/70 hover:bg-base-300"
            onClick={() => setSidebarOpen(true)}
          >
            {sidebarOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
          <span className="font-semibold">{t('admin.adminPanel')}</span>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
